'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {Camera, X, RefreshCw, AlertCircle, QrCode} from 'lucide-react';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

interface WebQRScannerProps {
  onScan: (result: string) => void;
  onClose: () => void;
  onError?: (error: string) => void;
  prompt?: string;
  formats?: string[];
  className?: string;
}

/**
 * WebQRScanner - Cross-platform QR/Barcode scanner using camera feed
 * 
 * Works on:
 * - Web browsers with webcam access
 * - Mobile Capacitor apps with camera permission
 * - Desktop Tauri apps with camera access
 * 
 * Uses html5-qrcode library for decoding (already installed in project)
 */
export function WebQRScanner({
  onScan,
  onClose,
  onError,
  prompt = 'Point camera at barcode',
  formats = ['QR_CODE', 'DATA_MATRIX', 'CODE_128', 'CODE_39', 'EAN_13', 'EAN_8', 'UPC_A', 'UPC_E'],
  className = ''
}: WebQRScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scannerRef = useRef<any>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Refs to hold latest callbacks without triggering effect re-runs
  const onScanRef = useRef(onScan);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);
  onScanRef.current = onScan;
  onCloseRef.current = onClose;
  onErrorRef.current = onError;
  
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cameraList, setCameraList] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  // Get available cameras
  const getCameras = useCallback(async () => {
    try {
      // First request permission to enumerate devices with labels
      await navigator.mediaDevices.getUserMedia({ video: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(d => d.kind === 'videoinput');
      setCameraList(cameras);
      
      // Prefer back camera on mobile
      const backCamera = cameras.find(c => 
        c.label.toLowerCase().includes('back') || 
        c.label.toLowerCase().includes('rear') ||
        c.label.toLowerCase().includes('environment')
      );
      
      if (backCamera) {
        setSelectedCamera(backCamera.deviceId);
      } else if (cameras.length > 0) {
        setSelectedCamera(cameras[0].deviceId);
      }
      
      return cameras;
    } catch (err: unknown) {
      console.error('[WebQRScanner] Error getting cameras:', err);
      throw err;
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setIsScanning(false);
  }, []);

  // Scan frame using html5-qrcode
  const scanFrame = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || !scannerRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx || video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationFrameRef.current = requestAnimationFrame(scanFrame);
      return;
    }
    
    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    try {
      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      // Use html5-qrcode's internal decoder
      const Html5Qrcode = scannerRef.current;
      
      // Create blob from canvas for scanning
      canvas.toBlob(async (blob) => {
        if (!blob) {
          animationFrameRef.current = requestAnimationFrame(scanFrame);
          return;
        }
        
        try {
          const file = new File([blob], 'frame.jpg', { type: 'image/jpeg' });
          const result = await Html5Qrcode.scanFile(file, false);
          
          if (result) {
            setLastResult(result);
            setIsScanning(false);
            stopCamera();
            onScanRef.current(result);
          } else {
            animationFrameRef.current = requestAnimationFrame(scanFrame);
          }
        } catch (scanErr) {
          // No QR code found in this frame, continue scanning
          animationFrameRef.current = requestAnimationFrame(scanFrame);
        }
      }, 'image/jpeg', 0.9);
      
    } catch (err) {
      // Continue scanning on next frame
      animationFrameRef.current = requestAnimationFrame(scanFrame);
    }
  }, [stopCamera]);

  // Alternative scanning using jsQR (simpler, more reliable)
  const scanFrameJsQR = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) {
      animationFrameRef.current = requestAnimationFrame(scanFrameJsQR);
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx || video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationFrameRef.current = requestAnimationFrame(scanFrameJsQR);
      return;
    }
    
    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    try {
      // Get image data for jsQR
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      // Dynamically import jsQR (it's a peer dependency of html5-qrcode)
      const jsQR = (await import('jsqr')).default;
      
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert'
      });
      
      if (code && code.data) {
        setLastResult(code.data);
        setIsScanning(false);
        stopCamera();
        onScanRef.current(code.data);
        return;
      }
    } catch (err) {
      // Continue scanning
    }
    
    animationFrameRef.current = requestAnimationFrame(scanFrameJsQR);
  }, [stopCamera]);

  const startScanning = useCallback(() => {
    setIsScanning(true);
    animationFrameRef.current = requestAnimationFrame(scanFrameJsQR);
  }, [scanFrameJsQR]);

  const startCamera = useCallback(async (deviceId?: string) => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const constraints: MediaStreamConstraints = {
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : { facingMode: 'environment' }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setError(null);
      setIsScanning(true);
      startScanning();
    } catch (err: unknown) {
      console.error('[WebQRScanner] Camera error:', err);
      const errName = err instanceof Error ? err.name : '';
      const errMessage = err instanceof Error ? err.message : String(err);

      if (errName === 'NotAllowedError') {
        setError('Camera permission denied. Please allow camera access and try again.');
      } else if (errName === 'NotFoundError') {
        setError('No camera found on this device.');
      } else if (errName === 'NotReadableError') {
        setError('Camera is in use by another application.');
      } else {
        setError('Failed to access camera: ' + errMessage);
      }

      onErrorRef.current?.(errMessage);
    }
  }, [startScanning]);

  // Initialize scanner — runs once on mount
  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
      try {
        // Check if camera is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setError('Camera is not supported in this browser. Please use a modern browser with camera support.');
          setIsInitializing(false);
          return;
        }
        
        // Load html5-qrcode for static scanning fallback
        try {
          const { Html5Qrcode } = await import('html5-qrcode');
          scannerRef.current = Html5Qrcode;
        } catch (err) {
          console.warn('[WebQRScanner] html5-qrcode not available, using jsQR only');
        }
        
        // Get available cameras
        await getCameras();
        
        if (mounted) {
          setIsInitializing(false);
          // Auto-start camera
          setTimeout(() => startCamera(), 100);
        }
      } catch (err: unknown) {
        console.error('[WebQRScanner] Init error:', err);
        if (mounted) {
          setError('Failed to initialize camera: ' + (err instanceof Error ? err.message : String(err)));
          setIsInitializing(false);
        }
      }
    };
    
    init();
    
    return () => {
      mounted = false;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switch camera
  const switchCamera = useCallback(() => {
    const currentIndex = cameraList.findIndex(c => c.deviceId === selectedCamera);
    const nextIndex = (currentIndex + 1) % cameraList.length;
    const nextCamera = cameraList[nextIndex];
    
    if (nextCamera) {
      setSelectedCamera(nextCamera.deviceId);
      stopCamera();
      startCamera(nextCamera.deviceId);
    }
  }, [cameraList, selectedCamera, stopCamera, startCamera]);

  // Retry after error
  const handleRetry = useCallback(() => {
    setError(null);
    setIsInitializing(true);
    getCameras().then(() => {
      setIsInitializing(false);
      startCamera(selectedCamera || undefined);
    }).catch((err: unknown) => {
      setError('Failed to access camera: ' + (err instanceof Error ? err.message : String(err)));
      setIsInitializing(false);
    });
  }, [getCameras, startCamera, selectedCamera]);

  return (
    <div className={`relative bg-black rounded-xl overflow-hidden ${className}`}>
      {/* Video feed */}
      <video 
        ref={videoRef}
        className="w-full h-full object-cover"
        playsInline
        muted
        autoPlay
      />
      
      {/* Hidden canvas for frame processing */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Scan overlay */}
      {isScanning && (
        <div className="absolute inset-0 pointer-events-none">
          {/* Darkened edges */}
          <div className="absolute inset-0 bg-black/40" />
          
          {/* Clear center area */}
          <div className="absolute inset-1/4 bg-transparent" style={{
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)'
          }} />
          
          {/* Scan frame corners */}
          <div className="absolute inset-1/4">
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-blue-500 rounded-tl-lg" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-blue-500 rounded-tr-lg" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-blue-500 rounded-bl-lg" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-blue-500 rounded-br-lg" />
            
            {/* Scanning line animation */}
            <div className="absolute left-0 right-0 h-0.5 bg-blue-500 animate-pulse" 
              style={{ 
                top: '50%',
                animation: 'scanLine 2s ease-in-out infinite'
              }} 
            />
          </div>
          
          {/* Prompt text */}
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <span className="px-4 py-2 bg-black/60 text-white text-sm rounded-full">
              {prompt}
            </span>
          </div>
        </div>
      )}
      
      {/* Loading state */}
      {isInitializing && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
          <div className="text-center">
            <BrandedLoadingSpinner mode="compact" size="lg" showSurface={false} showProgressBar={false} />
            <p className="text-gray-300">Initializing camera...</p>
          </div>
        </div>
      )}
      
      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 p-6">
          <div className="text-center max-w-sm">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-300 text-sm mb-4">{error}</p>
            <button
              onClick={handleRetry}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      )}
      
      {/* Controls */}
      <div className="absolute top-4 right-4 flex gap-2">
        {/* Switch camera button (if multiple cameras) */}
        {cameraList.length > 1 && (
          <button
            onClick={switchCamera}
            className="p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
            title="Switch camera"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        )}
        
        {/* Close button */}
        <button
          onClick={() => {
            stopCamera();
            onCloseRef.current();
          }}
          className="p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
          title="Close scanner"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Last result display */}
      {lastResult && (
        <div className="absolute bottom-16 left-4 right-4">
          <div className="p-3 bg-green-900/80 border border-green-600 rounded-lg">
            <p className="text-xs text-green-400 font-medium mb-1">Scanned:</p>
            <p className="text-sm text-green-200 font-mono break-all">{lastResult}</p>
          </div>
        </div>
      )}
      
      {/* CSS for scan line animation */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes scanLine {
          0%, 100% { transform: translateY(-50px); opacity: 0.5; }
          50% { transform: translateY(50px); opacity: 1; }
        }
      `}} />
    </div>
  );
}

export default WebQRScanner;
